import type { SpecialType } from '@iheartjane/types';
import compact from 'lodash-es/compact';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import {
  useCategoriesForRulesets,
  useProductsById,
} from '@jane/business-admin/data-access';
import type {
  ConditionApplyToOption,
  ConditionSection,
  ConditionSet,
} from '@jane/shared-b2b/components';
import { getSelectedValues } from '@jane/shared-b2b/components';
import { lineageOptions, weightValueOptions } from '@jane/shared-b2b/util';

import { toCategorySection } from './util/toCategorySection';
import { PRODUCTS_KEY, toProductsSection } from './util/toProductsSection';

export interface UseConditionSectionsArgs {
  /** The editor's groups, read for the ids whose rows the sections need fetched */
  conditionSets: ConditionSet[];
  isGlobal: boolean;
  orgId?: number;
  /** The products section's modal. Memoize it: every section rebuilds when it changes */
  renderProductsModal: () => ReactNode;
  /** Bulk pricing pairs weight with price; every other type keeps them apart */
  specialType?: SpecialType | '';
  storeId: string;
}

/**
 * Orchestrates fetching and assembling the data needed to populate the condition
 * sections, per "Apply to" choice a condition group can be set to.
 */
export const useConditionSections = ({
  conditionSets,
  isGlobal,
  orgId,
  renderProductsModal,
  specialType,
  storeId,
}: UseConditionSectionsArgs): ConditionApplyToOption[] => {
  const { data: categories } = useCategoriesForRulesets({
    isGlobal,
    orgId,
    storeId,
  });

  /** Every group's selected products, included and excluded, in one fetch */
  const productIds = useMemo(
    () => [
      ...new Set([
        ...getSelectedValues(conditionSets, 'excludes', PRODUCTS_KEY),
        ...getSelectedValues(conditionSets, 'includes', PRODUCTS_KEY),
      ]),
    ],
    [conditionSets]
  );

  // Paginated sections list only the selection, so their tags need the rows
  const { products: selectedProducts } = useProductsById({
    ids: productIds,
    isGlobal,
    orgId,
    storeId,
  });

  return useMemo(
    () => [
      {
        conditions: compact([
          toCategorySection(categories),
          {
            allowExcludes: true,
            items: lineageOptions(),
            key: 'lineages',
            label: 'Lineage',
            modalVariant: 'standard-dialogue',
            type: 'multiselect',
          } satisfies ConditionSection,
          specialType !== 'bulk_pricing' && {
            allowExcludes: true,
            items: weightValueOptions(),
            key: 'weight_values',
            label: 'Weights',
            type: 'multiselect',
          },
          specialType !== 'bulk_pricing' && {
            allowedDecimalPlaces: 2,
            items: [
              {
                label: 'Price',
                min: 0,
              },
            ],
            key: 'price',
            label: 'Price',
            startUnit: '$',
            step: 0.01,
            type: 'range',
          },
          specialType === 'bulk_pricing' && {
            allowedDecimalPlaces: 2,
            items: weightValueOptions(),
            key: 'weight_and_price',
            label: 'Weight and price',
            range: {
              label: 'Price',
              min: 0,
            },
            startUnit: '$',
            step: 0.01,
            type: 'multiselect-range',
          },
          toProductsSection(
            selectedProducts,
            renderProductsModal,
            'attributes'
          ),
        ]),
        label: 'Category, brand, and more',
        value: 'attributes',
      },
      {
        conditions: [
          toProductsSection(selectedProducts, renderProductsModal, 'products'),
        ],
        label: 'Individual products',
        value: 'products',
      },
    ],
    [categories, renderProductsModal, selectedProducts, specialType]
  );
};
